(() => {
  const root = document.querySelector('[data-sf-guided-collection]');
  if (!root) return;

  /* ---------- 0. Always open at the start of the flow ---------- */
  if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
  if (location.hash && location.hash.indexOf('#sf-gc-') === 0) {
    history.replaceState(null, '', location.pathname + location.search);
  }
  window.scrollTo(0, 0);
  window.addEventListener('pageshow', (e) => { if (e.persisted) window.scrollTo(0, 0); });
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isMobile = () => window.matchMedia('(max-width: 749px)').matches;

  /* ---------- 1. Tap a card -> smooth jump -> thin blue border on landing ---------- */
  let current = null;
  let armed = false;

  const clearHighlight = () => {
    if (current) current.classList.remove('sf-gc-hl');
    current = null;
    armed = false;
  };

  /* after the border appears, the next scroll / tap / key hides it */
  const armHide = () => {
    setTimeout(() => { armed = true; }, 600);   // ignore the tail end of the jump scroll
  };
  const hideOnActivity = () => { if (armed) clearHighlight(); };
  window.addEventListener('scroll', hideOnActivity, { passive: true });
  window.addEventListener('touchstart', hideOnActivity, { passive: true });
  window.addEventListener('pointerdown', hideOnActivity, { passive: true });
  window.addEventListener('keydown', hideOnActivity);

  const highlight = (section) => {
    clearHighlight();
    const el = section.querySelector('.sf-gc__feature-card')
            || section.querySelector('.sf-gc__cards > *, .sf-gc__product-grid > *');
    if (!el) return;
    void el.offsetWidth;                         // restart blink if tapped again
    el.classList.add('sf-gc-hl');
    current = el;
    armHide();
  };

  /* wait until the page has stopped scrolling */
  const whenScrollEnds = (cb) => {
    let done = false, timer;
    const finish = () => {
      if (done) return;
      done = true;
      window.removeEventListener('scroll', onScroll);
      cb();
    };
    const onScroll = () => { clearTimeout(timer); timer = setTimeout(finish, 140); };
    window.addEventListener('scroll', onScroll, { passive: true });
    if ('onscrollend' in window) window.addEventListener('scrollend', finish, { once: true });
    timer = setTimeout(finish, 900);
    setTimeout(finish, 2500);
  };

  root.addEventListener('click', (e) => {
    const link = e.target.closest('[data-sf-scroll]');
    if (!link) return;
    const href = link.getAttribute('href');
    if (!href || href.charAt(0) !== '#') return;
    const target = document.querySelector(href);
    if (!target) return;
    e.preventDefault();
    clearHighlight();

    target.classList.add('is-in');
    target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    if (target.id === 'sf-gc-top') return;
    whenScrollEnds(() => highlight(target));
  });

  /* ---------- 1b. Fundamentals: tap -> colour wash + icon bounce ---------- */
  root.querySelectorAll('.sf-gc__trust').forEach((tile) => {
    tile.addEventListener('click', () => {
      root.querySelectorAll('.sf-gc__trust.is-tapped').forEach((t) => { if (t !== tile) t.classList.remove('is-tapped'); });
      tile.classList.remove('is-tapped');
      void tile.offsetWidth;                  // restart the bounce on every tap
      tile.classList.add('is-tapped');
      clearTimeout(tile._t);
      tile._t = setTimeout(() => tile.classList.remove('is-tapped'), 1400);
    });
  });

  /* ---------- 1c. Tap anywhere in a feature section -> open its page ---------- */
  root.querySelectorAll('.sf-gc__button--stretch').forEach((btn) => {
    const section = btn.closest('.sf-gc__moment');
    if (!section) return;
    section.setAttribute('data-sf-link', btn.getAttribute('href'));
    section.addEventListener('click', (e) => {
      if (e.target.closest('a, button')) return;          // real links work as normal
      const sel = window.getSelection && window.getSelection().toString();
      if (sel) return;                                     // don't navigate while selecting text
      window.location.href = btn.href;
    });
  });

  /* ---------- 2. Dots under every swipe row ---------- */
  root.querySelectorAll('.sf-gc__cards, .sf-gc__product-grid').forEach((rail) => {
    const cards = Array.from(rail.children);
    if (cards.length < 2) return;

    const dots = document.createElement('div');
    dots.className = 'sf-gc__dots';
    dots.setAttribute('role', 'tablist');
    dots.setAttribute('aria-label', 'Scroll cards');

    const buttons = cards.map((card, i) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'sf-gc__dot';
      b.setAttribute('aria-label', 'Show card ' + (i + 1));
      b.addEventListener('click', () => {
        const left = card.offsetLeft - (rail.clientWidth - card.offsetWidth) / 2;
        rail.scrollTo({ left: Math.max(0, left), behavior: reduceMotion ? 'auto' : 'smooth' });
      });
      dots.appendChild(b);
      return b;
    });
    rail.after(dots);

    let current = -1;
    const setCurrent = (i) => {
      if (i === current) return;
      current = i;
      buttons.forEach((b, j) => b.setAttribute('aria-current', j === i ? 'true' : 'false'));
      cards.forEach((c, j) => c.classList.toggle('is-current', j === i && isMobile()));
    };

    /* which card is nearest the centre of the rail */
    const update = () => {
      const max = rail.scrollWidth - rail.clientWidth;
      if (rail.scrollLeft <= 4) return setCurrent(0);
      if (rail.scrollLeft >= max - 4) return setCurrent(cards.length - 1);
      const mid = rail.scrollLeft + rail.clientWidth / 2;
      let best = 0, dist = Infinity;
      cards.forEach((c, i) => {
        const d = Math.abs(c.offsetLeft + c.offsetWidth / 2 - mid);
        if (d < dist) { dist = d; best = i; }
      });
      setCurrent(best);
    };

    let ticking = false;
    rail.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => { update(); ticking = false; });
    }, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    update();
  });

  /* ---------- 3. Light section fade-in (opacity only, no movement) ---------- */
  if (reduceMotion || !('IntersectionObserver' in window)) return;
  const blocks = root.querySelectorAll('[data-sf-reveal]');
  root.classList.add('is-js');
  const io = new IntersectionObserver((entries) => {
    for (const en of entries) {
      if (en.isIntersecting) { en.target.classList.add('is-in'); io.unobserve(en.target); }
    }
  }, { rootMargin: '0px 0px 15% 0px' });
  blocks.forEach((el) => io.observe(el));
})();